import { getDefaultClient } from '@dynamic-labs-sdk/client';
import {
  MethodNotImplementedError,
  getWalletProviderFromWalletAccount,
} from '@dynamic-labs-sdk/client/core';
import type {
  SendOptions,
  Transaction,
  VersionedTransaction,
} from '@solana/web3.js';

import type { SolanaWalletAccount } from '../SolanaWalletAccount.types';
import { isSolanaWalletProvider } from '../isSolanaWalletProvider';
import { NotSolanaProviderError } from '../errors/NotSolanaProviderError';

type SignAndSendSponsoredTransactionParams = {
  options?: SendOptions;
  transaction: Transaction | VersionedTransaction;
  walletAccount: SolanaWalletAccount;
};

/**
 * Signs and sends a sponsored Solana transaction.
 * The transaction will be sponsored via Dynamic's gas sponsorship API.
 *
 * @param params.transaction - The transaction to sponsor, sign, and send
 * @param params.walletAccount - The wallet account to sign and send the transaction with
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns An object containing the signature
 * @throws {NotSolanaProviderError} If the wallet provider is not a Solana provider
 * @throws {MethodNotImplementedError} If the wallet provider does not implement signAndSendSponsoredTransaction
 */
export const signAndSendSponsoredTransaction = async (
  {
    walletAccount,
    transaction,
    options,
  }: SignAndSendSponsoredTransactionParams,
  client = getDefaultClient()
): Promise<{ signature: string }> => {
  const provider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  if (!isSolanaWalletProvider(provider)) {
    throw new NotSolanaProviderError({ providerKey: provider.key });
  }

  if (!provider.signAndSendSponsoredTransaction) {
    throw new MethodNotImplementedError('signAndSendSponsoredTransaction');
  }

  return provider.signAndSendSponsoredTransaction({
    options,
    transaction,
    walletAccount,
  });
};
